const clampToZero = (value) => (value > 0 ? value : 0);
/**
 * Computes how much screen time is left right now.
 *
 * SPEC.md listed several independent limits without saying how they interact.
 * They interact as a minimum: the tightest constraint wins, and the caller is
 * told which one it was.
 *
 * This is the single definition of "remaining time" in the system. The backend
 * renders the parent dashboard from it and the Kids App enforces playback with
 * it; computing it twice would let the two drift apart invisibly.
 */
export const computeRemaining = (input) => {
    const { budget, spent, contentType, sessionElapsedSeconds, secondsUntilWindowEnd } = input;
    const candidates = [
        {
            seconds: clampToZero(budget.dailyTotalSeconds - spent.totalSeconds),
            factor: 'DAILY_TOTAL',
        },
        {
            seconds: clampToZero(budget.maxSessionSeconds - sessionElapsedSeconds),
            factor: 'SESSION',
        },
    ];
    const typeBudget = budget.perContentTypeSeconds[contentType];
    if (typeBudget !== undefined) {
        candidates.push({
            seconds: clampToZero(typeBudget - (spent.byContentType[contentType] ?? 0)),
            factor: 'CONTENT_TYPE',
        });
    }
    if (secondsUntilWindowEnd !== null) {
        candidates.push({
            seconds: clampToZero(secondsUntilWindowEnd),
            factor: 'SCHEDULE',
        });
    }
    return candidates.reduce((tightest, candidate) => candidate.seconds < tightest.remainingSeconds
        ? { remainingSeconds: candidate.seconds, limitingFactor: candidate.factor }
        : tightest, { remainingSeconds: Number.POSITIVE_INFINITY, limitingFactor: 'NONE' });
};
/** Whether the daily allowance across every content type is spent. */
export const isDailyBudgetExhausted = (budget, spent) => spent.totalSeconds >= budget.dailyTotalSeconds;
/** Whether the allowance for one content type is spent. */
export const isContentTypeBudgetExhausted = (budget, spent, contentType) => {
    const limit = budget.perContentTypeSeconds[contentType];
    return limit !== undefined && (spent.byContentType[contentType] ?? 0) >= limit;
};
/** Whether the current session has reached the uninterrupted-length ceiling. */
export const isSessionLimitReached = (budget, sessionElapsedSeconds) => sessionElapsedSeconds >= budget.maxSessionSeconds;
//# sourceMappingURL=budget.js.map